import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Paper, CircularProgress } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../index';

// Components
import StatsCards from '../components/dashboard/StatsCards';
import RecentDeals from '../components/dashboard/RecentDeals';
import MakeDistribution from '../components/dashboard/MakeDistribution';
import PriceRangeDistribution from '../components/dashboard/PriceRangeDistribution';
import SavingsStats from '../components/dashboard/SavingsStats';
import WelcomeCard from '../components/dashboard/WelcomeCard';
import { useAuth } from '../context/AuthContext';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { userData } = useAuth();
  
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const getDashboardStats = httpsCallable(functions, 'api-getDashboardStats');
        const result = await getDashboardStats();
        
        if (result.data.success) {
          setStats(result.data.stats);
        } else {
          setError('Failed to load dashboard data');
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        setError('An error occurred while loading dashboard data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardStats();
  }, []);
  
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <Typography color="error" variant="h5">
          {error}
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      
      <Grid container spacing={3}>
        {/* Welcome Card */}
        <Grid item xs={12}>
          <WelcomeCard 
            userName={userData?.displayName || 'there'} 
            lastUpdate={stats.lastUpdate} 
          />
        </Grid>
        
        {/* Stats Cards */}
        <Grid item xs={12}>
          <StatsCards 
            totalVehicles={stats.totalVehicles} 
            goodDeals={stats.goodDeals} 
            avgSavings={stats.averageSavings.amount} 
            avgDiscount={stats.averageSavings.percentage} 
          />
        </Grid>
        
        {/* Charts Row */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Make Distribution
            </Typography>
            <MakeDistribution data={stats.makeDistribution} />
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Price Range Distribution
            </Typography>
            <PriceRangeDistribution data={stats.priceRangeDistribution} />
          </Paper>
        </Grid>
        
        {/* Savings Stats */}
        <Grid item xs={12} md={4}>
          <SavingsStats 
            avgSavingsAmount={stats.averageSavings.amount} 
            avgSavingsPercentage={stats.averageSavings.percentage} 
          />
        </Grid>
        
        {/* Recent Deals */}
        <Grid item xs={12} md={8}>
          <RecentDeals deals={stats.recentDeals} />
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;